<template>

    <div>

        <v-snackbar
            v-model="filtersMenuModel"
            :bottom="true"
            :color="filterValid ? 'primary' : 'red darken-1'"
            :timeout="-1"
            class="ma-0 pa-0"
        >
            <div style="width: 100%" class="text-md-center">
                <v-btn @click="filtersMenuOpen=true"
                       style="width: 100%"
                       text
                       class="pa-2 ma-0">
                    <v-icon class="mr-4">mdi-arrow-up-bold</v-icon>
                    FILTERING OPTIONS
                    <v-icon class="ml-4">mdi-arrow-up-bold</v-icon>
                </v-btn>
            </div>

        </v-snackbar>

        <div class="floatingStatusBar">

            <toolbar-page title="Segment builder" help="segment-builder">
                <template slot="buttons">

                    <!-- ACTION BUTTONS -->
                    <v-card flat class="d-flex" color="transparent">

                        <v-btn color="primary"
                               @click="saveClicked"
                               :loading="saving">
                            {{ saveButtonText }}
                        </v-btn>

                        <v-btn fab x-small class="ml-4" color="red" @click="cancelClicked">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>

                    </v-card>
                </template>

            </toolbar-page>

            <v-container fluid v-if="sessionActive && hasPermission('run queries')" class="pl-0 pr-0 pt-1 pb-1">

                <div class="d-flex flex-row flex-wrap pl-4">

                    <!-- MATCH COUNT -->
                    <v-card flat color="transparent" width="300" class="pr-2 pb-1 pt-2">
                        <stat-box-numerical :loading="queryRunning" hint="Match count" :value="matches"></stat-box-numerical>
                    </v-card>

                    <!-- EXECUTION TIME -->
                    <v-card flat color="transparent" width="200" class="pr-2 pt-2 pb-1">
                        <stat-box-numerical :loading="queryRunning" hint="Execution time"
                                  :value="executionTime"
                                  postfix="ms"></stat-box-numerical>
                    </v-card>

                    <!-- RESPONSE RATE -->
                    <v-card v-if="targetCompletes !== null && responseRate > 0" flat color="transparent" width="200" class="pr-2 pt-2 pb-1">
                        <v-tooltip color="primary" bottom max-width="250">
                            <template v-slot:activator="{ on }">
                                <div v-on="on" class="cursor-help">
                                    <v-scale-transition>
                                        <stat-box-numerical :loading="queryRunning"
                                                            hint="Response rate"
                                                            :value="responseRateFormatted"
                                                            :color="calculateResponseRateColor(responseRate)"
                                                            postfix="%"
                                                            :show-info-icon="true"
                                        ></stat-box-numerical>
                                    </v-scale-transition>
                                </div>
                            </template>
                            Estimated response rate for this segment. The response rate means that indicated % of population is likely to
                            complete this project. The response rate is calculated using formula invited / completed * 100 %.
                        </v-tooltip>
                    </v-card>

                    <!-- FEASIBILiTY MULTIPLIER -->
                    <v-card v-if="targetCompletes !== null && responseRate > 0" flat color="transparent" width="200" class="pr-2 pt-2 pb-1">
                        <v-tooltip color="primary" bottom max-width="250">
                            <template v-slot:activator="{ on }">
                                <v-scale-transition>
                                    <div v-on="on" class="cursor-help">
                                        <stat-box-numerical
                                            :loading="queryRunning"
                                            hint="Sample availability"
                                            :value="feasibilityMultiplier"
                                            :color="calculateFeasibilityColor(matches,targetCompletes,responseRate)"
                                            prefix-icon="mdi-close"
                                            :show-info-icon="true"
                                        ></stat-box-numerical>
                                    </div>
                                </v-scale-transition>
                            </template>
                            Sample availability tells you how many times over you could run this qualification without running out of
                            sample.
                        </v-tooltip>
                    </v-card>

                    <!-- BUSINESS RULES -->
                    <v-card flat color="transparent" width="270" class="pr-2 pt-2 pb-1"
                            v-if="showBusinessRules && businessRulesActive">
                        <v-tooltip color="primary" bottom>
                            <template v-slot:activator="{ on }">
                                <div v-on="on">
                                    <business-rules-box
                                        :enabled="data.type!=='filter'"
                                        :value="businessRules"
                                        :json-query="json_query"
                                        :json-filter="json_filter"
                                        @change="businessRulesToggled">
                                    </business-rules-box>
                                </div>
                            </template>
                            Click to toggle business rules on / off
                        </v-tooltip>
                    </v-card>

                    <!-- ACTIVITY FILTER -->
                    <v-card flat color="transparent" width="270" class="pr-2 pt-2 pb-1">
                        <filter-status-box
                            :filter="filter"
                            @change="filtersChanged">
                        </filter-status-box>
                    </v-card>

                </div>

            </v-container>

            <!-- SUB PANEL SELECTOR -->
            <sub-panel-selector v-if="!disableSubPanelFiltering"
                                class="pl-4"
                                :sub-panels="subPanels"
                                :selected="filter.includeSubpanels"
                                @change="subPanelsChanged"
            ></sub-panel-selector>

        </div>

        <v-container fluid class="pa-0" v-if="sessionActive && hasPermission('run queries')">

            <!-- SPACER -->
            <v-card flat color="transparent" :height="statusBarSpacerHeight"></v-card>

            <!-- QUERY BUILDER -->
            <v-progress-linear v-if="queryRunning"
                               class="ma-0 grey darken-3"
                               value=100
                               color="blue"
                               height="3"
                               :indeterminate="true"
            ></v-progress-linear>

            <v-progress-linear v-if="!queryRunning"
                               class="ma-0"
                               value=100
                               color="grey darken-3"
                               height="3"
            ></v-progress-linear>

            <statement
                :query="query"
                :data-variables="dataVariables"
                @changed="statementChanged"
            >
            </statement>

            <v-card flat v-if="form">
                <v-card-text class="pb-0">
                    <v-row>

                        <v-col cols="12" md="5" lg="4" xl="3">

                            <v-text-field
                                v-model="data.name"
                                label="Segment name"
                                placeholder="Please enter name"
                                @keyup.enter="enterPressedInNameField"
                            ></v-text-field>

                        </v-col>

                        <v-col grow>

                        </v-col>

                        <v-col cols="12" md="3" lg="2">

                            <v-switch
                                v-model="data.type"
                                false-value="query"
                                true-value="filter"
                                color="primary"
                                @change="filterSwitchChanged"
                                label="Publish as filter">
                            </v-switch>
                        </v-col>

                        <v-col cols="12" md="3" lg="2">
                            <v-switch v-model="data.shared" color="primary" label="Share with team">

                            </v-switch>
                        </v-col>
                    </v-row>

                </v-card-text>
            </v-card>

            <v-card v-if="debug">
                <v-card-title>
                    <v-icon large class="mr-2">mdi-bug</v-icon>
                    <h3>Debug</h3>
                </v-card-title>
                <v-card-text>
                    <v-alert class="pa-0 body-2" type="info">Query</v-alert>
                    <vue-json-pretty :data="query"></vue-json-pretty>
                    <v-alert class="pa-0 body-2" type="info">Filter</v-alert>
                    <vue-json-pretty :data="filter"></vue-json-pretty>
                    <v-alert class="pa-0 body-2" type="info">Data</v-alert>
                    <vue-json-pretty :data="data"></vue-json-pretty>
                    <v-alert class="pa-0 body-2" type="info">Error messages</v-alert>
                    <vue-json-pretty :data="errorMessages"></vue-json-pretty>
                </v-card-text>
            </v-card>

        </v-container>

        <filters-menu
            :open="filtersMenuOpen"
            @close="filtersMenuOpen=false"
            :filter="filter"
            @change="filtersChanged"
            @error="filtersError"
        ></filters-menu>

        <json v-if="false" :data="subPanels"/>
    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapState, mapActions, mapGetters, mapMutations} from 'vuex';
import Form from '../../../components/forms/Form';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import Statement from './Statement';
import FilterStatusBox from './FiltersStatusBox';
import BusinessRulesBox from './BusinessRulesBox';
import VueJsonPretty from 'vue-json-pretty';
import FiltersMenu from "./FiltersMenu";
import SubPanelSelector from "../../../components/UI/SubPanelSelector";
import QueryBuffer from "../ts/QueryBuffer";
import Help from '../../../modules/Help/components/index';
import {rateColorMixin} from "../../../components/mixins/rateColorMixin";
import {feasibilityColorMixin} from "../../../components/mixins/feasilityColorMixin";
import StatBoxNumerical from "../../../components/UI/StatBoxNumerical";

export default {

    mixins: [activeSessionChecker, rateColorMixin, feasibilityColorMixin],

    components: {
        StatBoxNumerical,
        FiltersMenu,
        ToolbarPage,
        Statement,
        VueJsonPretty,
        FilterStatusBox,
        BusinessRulesBox,
        SubPanelSelector,
        Help,
        Json
    },

    props: {
        id: {
            required: false,
            type: Number,
            default: null,
        },
        json_query: {
            type: Object | null,
            required: false,
            default: () => {
                return {booleanOperation: "must"};
            }
        },
        json_filter: {
            type: Object | null,
            required: false,
            default: () => {
                return {};
            }
        },
        name: {
            type: String,
            required: false,
            default: 'My panelist segment',
        },
        shared: {
            required: false,
            type: Boolean,
            default: false,
        },
        type: {
            required: false,
            type: String,
            default: 'query',
        },
        created_by: {
            required: false,
            type: Object,
            default: () => null
        },
        saveButtonText: {
            required: false,
            type: String,
            default: 'SAVE'
        },
        cancelButtonText: {
            required: false,
            type: String,
            default: 'CANCEL'
        },
        showBusinessRules: {
            required: false,
            type: Boolean,
            default: true
        },
        targetCompletes: {
            required: false,
            type: Number | null,
            default: null,
        },
        disableSubPanelFiltering: {
            required: false,
            type: Boolean,
            default: false,
        },
        showExecutionTime: {
            required: false,
            type: Boolean,
            default: true,
        },
        saving: {
            required: false,
            type: Boolean,
            default: false
        },
        form: {
            required: false,
            type: Boolean,
            default: true
        },
        errorMessages: {
            required: false,
            type: Object,
            default: () => {
                return {}
            }
        }
    },

    data() {
        return {
            debug: false,
            query: null,
            filter: {
                active: false,
                excludeDays: 7,
                excludeOperation: "must_not",
                excludeStatuses: ['invited'],
                excludeCategoriesActive: false,
                excludeCategoriesDays: 31,
                excludeCategories: [],
                excludeCategoriesStatuses: [],
                includeSubpanels: [],
                includeSubpanelsActive: false,
                projectFilterActive: false,
                projectFilterOperation: "must",
                projectFilterIds: null,
            },
            data: {
                name: 'My Definition',
                type: 'query'
            },
            activityFilter: null,
            businessRules: null,
            filtersMenuOpen: false,
            filtersMenuModel: true,
            filterValid: true,
            lastUsedConfig: null,
            queryBuffer: new QueryBuffer(1000),
            helpDialogModel: false
        }
    },
    computed: {

        // Root store
        ...mapState([
            'dataVariables',
        ]),

        ...mapState('SubPanels', [
            'subPanels',
        ]),

        ...mapState('Segments', [
            'matches',
            'queryRunning',
            'executionTime',
            'responseRate'
        ]),

        ...mapGetters('PanelSettings', [
            'businessRulesActive'
        ]),

        responseRateFormatted() {
            if (this.responseRate > 10) {
                return Math.round(this.responseRate);
            }
            return Math.round(this.responseRate * 10) / 10;
        },

        queryValid() {
            if (this.query.conditions && this.query.conditions.length > 0) {
                let result = true;
                this.query.conditions.forEach(condition => {
                    if (!condition.dataVariableId) {
                        result = false;
                    }
                });
                return result;
            }
            return false;
        },

        feasibilityMultiplier() {
            if (this.responseRate) {
                return this.calculateFeasibilityMultiplierRounded(this.matches, this.targetCompletes, this.responseRate);
            }
            return 0;
        },

        statusBarSpacerHeight() {
            if (this.disableSubPanelFiltering) return 180;
            return this.subPanels && this.subPanels.length > 1 ? 240 : 180;
        }
    },

    methods: {

        ...mapMutations('Segments', [
            'setExecutionTime',
            'setMatchCount',
            'setResponseRate'
        ]),

        // Root store
        ...mapActions([
            'getDataVariables',
        ]),

        ...mapActions('SubPanels', [
            'loadSubPanels',
        ]),

        ...mapActions('Segments', [
            'runElasticSearchQuery',
        ]),

        filterSwitchChanged(type) {
            this.data.type = type;
            this.queryBuffer = new QueryBuffer(0);
            this.runQuery();

            // Toggle business rules on / off depending on the type
            this.businessRules = type === "query";
        },

        statementChanged(newStatement) {
            this.query = newStatement;
            this.runQuery();
        },

        businessRulesToggled(businessRules) {
            this.businessRules = businessRules;
            this.runQuery();
        },

        subPanelsChanged(ids) {
            if (ids.length > 0) {
                this.filter.includeSubpanels = ids;
                this.filterValid = true;
                this.filter.includeSubpanelsActive = true;
            } else {
                this.filter.includeSubpanelsActive = false;
            }

            this.$emit('change:selected-sub-panels', ids);
            this.runQuery();
        },

        runQuery() {

            if (this.queryValid && this.filterValid) {

                if (!this.showBusinessRules) {
                    this.businessRules = false;
                }

                const config = {
                    query: this.query,
                    filter: this.filter,
                    businessRulesActive: this.businessRules
                };

                this.queryBuffer.insert(config, this.sendQueryRequest);
            }
        },

        sendQueryRequest(config) {

            let form = new Form(config);

            this.runElasticSearchQuery(form)
                .then(() => {

                })
                .catch(error => {
                    if (error.status === 400) {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            delay: 5000,
                            message: error.data.message
                        });
                    } else {
                        Bus.$emit('server-error');
                    }
                });
        },

        filtersError(error) {
            this.filterValid = false;
        },

        filtersChanged(filter) {

            this.filter = Object.assign(this.filter, filter);
            this.filterValid = true;

            this.runQuery();
        },

        saveClicked() {

            if (!this.filterValid) {
                this.filtersMenuOpen = true;
                Bus.$emit('flash-message', {type: 'error', message: 'Filtering options contain errors'});
                // TODO Navigate to the correct page
            } else if (!this.queryValid) {
                Bus.$emit('flash-message', {type: 'error', message: 'Logic error detected'});
            } else {
                this.data.json_query = this.query;
                this.data.json_filter = this.filter;
                this.$emit('save', this.data);
            }
        },

        cancelClicked() {
            this.$emit('cancel');
        },

        buildFormFromProps() {
            this.data = {
                json_query: null,
                json_filter: null
            };

            if (this.form) {
                this.data.id = this.id;
                this.data.name = this.name;
                this.data.shared = this.shared;
                this.data.type = this.type;
            }

            this.query = this.json_query ? this.json_query : {booleanOperation: 'must'};

            if (this.json_filter) {
                this.filter = Object.assign(this.filter, this.json_filter);
            }

            this.businessRules = this.type === 'query';
        },

        enterPressedInNameField() {
            if (this.queryName.length < 5) {
                Bus.$emit('flash-message', {message: 'Name must be at least 5 characters longs'});
            }
            this.saveClicked();
        },

        helpClicked(id) {
            this.helpIdentifier = id;
            this.helpDialogModel = true;
        }
    },

    mounted() {

        this.setMatchCount(0);
        this.setExecutionTime(0);
        this.setResponseRate(0);
        this.getDataVariables();
        this.loadSubPanels();
    },

    created() {
        if (!this.$store.state.Segments) {
            this.$store.registerModule('Segments', Store);
        }

        this.buildFormFromProps();
    },
}

</script>

<style scoped>

.floatingStatusBar {
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    z-index: 4;
    background-color: rgba(0, 0, 0, 0.7);
}

</style>